import type { ZodTypeAny } from 'zod'
import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { NumberField } from '@app/components/core/form/fields/NumberField'
import { CheckboxField } from '@app/components/core/form/fields/CheckboxField'
import { CheckboxGroupField } from '@app/components/core/form/fields/CheckboxGroupField'
import { CreationGuide } from '@app/pages/dashboard/initiative/create/blocks/CreationGuide'
import { useTranslations } from '@i18n/useLanguage'
import { type EventSettingsFormValues } from '../shared/eventValidation'
import { eventCreationSteps, type EventCreationStep } from '../shared/workflow'
import { StepValuesSync } from '../shared/StepValuesSync'
import type { Visibility } from '../shared/eventTypes'
import { Image } from '@app/components/core/image/Image'
import GroupUsersIllustration from '@app/assets/images/illustrations/group-users.png'

const CURRENT_STEP: EventCreationStep = eventCreationSteps.eventSettings

type Props = {
  schema: ZodTypeAny
  defaultValues: EventSettingsFormValues
  onValuesChange: (values: EventSettingsFormValues) => void
}

export const EventSettingsStep = ({
  schema,
  defaultValues,
  onValuesChange,
}: Props) => {
  const t = useTranslations().app.dashboard.event.creation

  const stepTitles = { [CURRENT_STEP]: t.steps.eventSettings.title }
  const stepDescriptions = {
    [CURRENT_STEP]: t.steps.eventSettings.description,
  }


  const visibilityOptions: { value: Visibility; label: string }[] = [
    { value: 'followers', label: t.visibilityFollowers },
    { value: 'members', label: t.visibilityMembers },
    { value: 'public', label: t.visibilityPublic },
  ]

  return (
    <CreationGuide
      steps={[CURRENT_STEP]}
      currentStep={CURRENT_STEP}
      stepTitles={stepTitles}
      stepDescriptions={stepDescriptions}
      illustration={
        <Image
          src={GroupUsersIllustration}
          alt="Group Users Illustration"
          style={{ maxWidth: '15rem' }}
        />
      }
    >
      <Form<EventSettingsFormValues>
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2rem" alignItems="stretch">
          <StepValuesSync<EventSettingsFormValues>
            onValuesChange={onValuesChange}
          />
          <NumberField<EventSettingsFormValues>
            name="limit"
            label={t.limitLabel}
            placeholder={t.limitPlaceholder}
            min={1}
          />
          <CheckboxField<EventSettingsFormValues>
            name="enableWaitlist"
            label={t.enableWaitlistLabel}
            tooltipText={t.enableWaitlistTooltip}
            labelSize="small"
          />
          <CheckboxGroupField<EventSettingsFormValues, Visibility>
            name="visibility"
            label={t.visibilityLabel}
            options={visibilityOptions}
          />
          <CheckboxField<EventSettingsFormValues>
            name="reminder"
            label={t.reminderLabel}
            tooltipText={t.reminderTooltip}
            labelSize="small"
          />
        </Column>
      </Form>
    </CreationGuide>
  )
}
